/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Viewport & Panels Store (Zustand)
   Canvas pan, zoom, coordinate math, and UI panel visibility.
   ═══════════════════════════════════════════════════════════════ */

import { create } from 'zustand';

export interface ViewportTransform {
  x: number;
  y: number;
  zoom: number;
}

interface ViewportStore {
  // ── Transform ──
  x: number;
  y: number;
  zoom: number;

  setTransform: (transform: Partial<ViewportTransform>) => void;
  panBy: (dx: number, dy: number) => void;
  zoomBy: (factor: number, originX?: number, originY?: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetViewport: () => void;

  // ── Panels ──
  inspectorOpen: boolean;
  shelfOpen: boolean;
  previewOpen: boolean;
  toggleInspector: () => void;
  toggleShelf: () => void;
  togglePreview: () => void;
  setInspectorOpen: (open: boolean) => void;
  setShelfOpen: (open: boolean) => void;
  setPreviewOpen: (open: boolean) => void;

  // ── Coordinate Mapping ──
  screenToGraph: (
    screenX: number,
    screenY: number,
    containerRect: DOMRect
  ) => { x: number; y: number };
  graphToScreen: (
    graphX: number,
    graphY: number,
    containerRect: DOMRect
  ) => { x: number; y: number };
}

const ZOOM_MIN = 0.15;
const ZOOM_MAX = 3.0;

export const useViewportStore = create<ViewportStore>((set, get) => ({
  x: 0,
  y: 0,
  zoom: 1.0,

  setTransform: (transform) =>
    set((s) => ({
      x: transform.x !== undefined ? transform.x : s.x,
      y: transform.y !== undefined ? transform.y : s.y,
      zoom:
        transform.zoom !== undefined
          ? Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, transform.zoom))
          : s.zoom,
    })),

  panBy: (dx, dy) =>
    set((s) => ({
      x: s.x + dx,
      y: s.y + dy,
    })),

  zoomBy: (factor, originX, originY) => {
    const { x, y, zoom } = get();
    const newZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom * factor));
    if (newZoom === zoom) return;

    if (originX !== undefined && originY !== undefined) {
      // Zoom centered at cursor point
      const scale = newZoom / zoom;
      const newX = originX - (originX - x) * scale;
      const newY = originY - (originY - y) * scale;
      set({ x: newX, y: newY, zoom: newZoom });
    } else {
      set({ zoom: newZoom });
    }
  },

  zoomIn: () => get().zoomBy(1.2),
  zoomOut: () => get().zoomBy(1 / 1.2),
  resetViewport: () => set({ x: 0, y: 0, zoom: 1.0 }),

  // ── Panels ──
  inspectorOpen: true,
  shelfOpen: true,
  previewOpen: true,
  toggleInspector: () => set((s) => ({ inspectorOpen: !s.inspectorOpen })),
  toggleShelf: () => set((s) => ({ shelfOpen: !s.shelfOpen })),
  togglePreview: () => set((s) => ({ previewOpen: !s.previewOpen })),
  setInspectorOpen: (open) => set({ inspectorOpen: open }),
  setShelfOpen: (open) => set({ shelfOpen: open }),
  setPreviewOpen: (open) => set({ previewOpen: open }),

  // ── Coordinate Math ──
  screenToGraph: (screenX, screenY, containerRect) => {
    const { x, y, zoom } = get();
    const localX = screenX - containerRect.left;
    const localY = screenY - containerRect.top;
    return {
      x: (localX - x) / zoom,
      y: (localY - y) / zoom,
    };
  },

  graphToScreen: (graphX, graphY, containerRect) => {
    const { x, y, zoom } = get();
    return {
      x: graphX * zoom + x + containerRect.left,
      y: graphY * zoom + y + containerRect.top,
    };
  },
}));
